import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db, collection, getDocs, doc, updateDoc, deleteDoc, addDoc, getDoc } from '../config/firebase';
import { seedEvents, checkAndSeedEvents, clearEvents } from '../services/firestoreSeeder';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Role } from '../types/auth';
import TestEventCreation from '../components/TestEventCreation';
import { getReviewRecommendation, RecommendationResult } from '../services/eventReviewAI';
import { Event, User } from '../types/models';

const AdminDashboard: React.FC = () => {
  const { user, hasRole, hasPermission, addUserRole, removeUserRole } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [activeTab, setActiveTab] = useState<'users' | 'events' | 'pending' | 'ai'>('users');
  const [isLoading, setIsLoading] = useState(true);
  const [isSeeding, setIsSeeding] = useState(false);
  const [databaseStatus, setDatabaseStatus] = useState<{
    checked: boolean;
    hasEvents: boolean;
  }>({ checked: false, hasEvents: false });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pendingReviewEvent, setPendingReviewEvent] = useState<Event | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [eventSearch, setEventSearch] = useState('');
  const [eventStatusFilter, setEventStatusFilter] = useState('');
  const [eventOrganizerFilter, setEventOrganizerFilter] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState<string>('');
  const [showEventModal, setShowEventModal] = useState(false);
  const [modalEvent, setModalEvent] = useState<Event | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [reviewRecommendation, setReviewRecommendation] = useState<RecommendationResult | null>(null);

  // Fetch data with retry mechanism
  const fetchData = async (retryCount = 0) => {
    if (!user || !hasRole('admin')) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setErrorMessage(null);
      
      // Fetch users
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      const usersData = usersSnapshot.docs.map(doc => {
        const data = doc.data();
        // Ensure roles array exists and contains at least 'user' role
        const roles = data.roles || ['user'];
        if (!roles.includes('user')) {
          roles.push('user');
        }
        return {
          id: doc.id,
          ...data,
          roles
        };
      }) as User[];
      
      setUsers(usersData);
      
      // Fetch events
      const eventsRef = collection(db, 'events');
      const eventsSnapshot = await getDocs(eventsRef);
      const eventsData = eventsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Event[];
      
      setEvents(eventsData);
      setDatabaseStatus({ 
        checked: true, 
        hasEvents: eventsSnapshot.size > 0 
      });
      
    } catch (error: any) {
      console.error('Error fetching admin data:', error);
      
      // Retry logic
      if (retryCount < 3) {
        console.log(`Retrying fetch (attempt ${retryCount + 1})...`);
        setTimeout(() => fetchData(retryCount + 1), 1000 * (retryCount + 1));
        return;
      }
      
      setErrorMessage(`Failed to fetch data: ${error.message || 'Unknown error'}`);
      toast.error('Failed to fetch admin data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [user]);

  const handleSeedEvents = async () => {
    if (!hasRole('admin')) {
      toast.error('You do not have permission to seed events');
      return;
    }

    try {
      setIsSeeding(true);
      await seedEvents();
      await fetchData(); // Refresh data after seeding
      toast.success('Sample events added successfully');
    } catch (error) {
      console.error('Error seeding events:', error);
      toast.error('Failed to add sample events');
    } finally {
      setIsSeeding(false);
    }
  };

  const handleCheckEvents = async () => {
    if (!hasRole('admin')) {
      toast.error('You do not have permission to check events');
      return;
    }

    try {
      const needsSeeding = await checkAndSeedEvents();
      setDatabaseStatus({ 
        checked: true, 
        hasEvents: !needsSeeding
      });
      
      toast.info(!needsSeeding
        ? 'Events already exist in database' 
        : 'No events found in database'
      );
    } catch (error) {
      console.error('Error checking events:', error);
      toast.error('Failed to check events');
    }
  };

  const deleteEvent = async (eventId: string) => {
    if (!hasRole('admin')) {
      toast.error('You do not have permission to delete events');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      const eventRef = doc(db, 'events', eventId);
      await deleteDoc(eventRef);
      
      // Update local state
      setEvents(events.filter(event => event.id !== eventId));
      toast.success('Event deleted successfully');
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error('Failed to delete event');
    }
  };

  const updateUserRole = async (userId: string, role: Role, action: 'add' | 'remove') => {
    if (!hasRole('admin')) {
      toast.error('You do not have permission to manage user roles');
      return;
    }

    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      if (!userDoc.exists()) {
        toast.error('User not found');
        return;
      }

      const userData = userDoc.data() as User;
      let updatedRoles = userData.roles || [];

      if (action === 'add') {
        if (!updatedRoles.includes(role)) {
          updatedRoles = [...updatedRoles, role];
        }
      } else {
        // Prevent removing the 'admin' role from the primary admin account.
        const adminUserEmail = "msobhi412@gmail.com";
        if (userData.email === adminUserEmail && role === 'admin') {
          toast.error("Cannot remove the 'admin' role from the primary admin account.");
          return;
        }
        updatedRoles = updatedRoles.filter(r => r !== role);
      }
      
      // Ensure user always has at least the 'user' role
      if (!updatedRoles.includes('user')) {
        updatedRoles.push('user');
      }

      // Update the user document with new roles and userType
      await updateDoc(userRef, {
        roles: updatedRoles,
        userType: updatedRoles.includes('admin') ? 'admin' : updatedRoles.includes('organizer') ? 'organizer' : 'user',
        updatedAt: new Date(),
      });

      // Update the local state to reflect changes immediately
      setUsers(prevUsers =>
        prevUsers.map(u =>
          u.id === userId
            ? { ...u, roles: updatedRoles }
            : u
        )
      );

      toast.success(`User role ${action === 'add' ? 'added' : 'removed'} successfully`);
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error(`Failed to ${action} user role`);
    }
  };

  const handleApproveEvent = async (eventId: string) => {
    if (!hasRole('admin')) {
      toast.error('You do not have admin privileges.');
      return;
    }
    const event = events.find((e: Event) => e.id === eventId) as Event | undefined;
    if (!event) {
      toast.error('Event not found.');
      return;
    }
    if (!event.title || event.title.trim() === '' || !event.coverImage || event.coverImage.trim() === '') {
      toast.error('Cannot approve: Event must have a title and image.');
      return;
    }
    
    setActionLoading(eventId + '-approve');
    try {
      await updateDoc(doc(db, 'events', eventId), { status: 'published' });
      setEvents(prevEvents => prevEvents.map(ev => ev.id === eventId ? { ...ev, status: 'published' } : ev));
      await fetchData();
      toast.success('Event approved');
      if (event.organizerId) {
        notifyOrganizer(eventId, event.organizerId, 'approved');
      }
    } catch (error) {
      console.error('Failed to approve event:', error);
      const errMsg = (error && (error as any).message) ? (error as any).message : String(error);
      toast.error('Failed to approve event: ' + errMsg);
    } finally {
      setActionLoading(null);
    }
  };
  
  const handleRejectEvent = async (eventId: string, reason: string) => {
    if (!hasRole('admin')) {
        toast.error('You do not have admin privileges.');
        return;
    }
    setActionLoading(eventId + '-reject');
    try {
      await updateDoc(doc(db, 'events', eventId), { status: 'rejected', rejectionReason: reason });
      setShowRejectModal(false);
      setPendingReviewEvent(null);
      setRejectionReason('');
      await fetchData(); // Refetch events after action
      toast.success('Event rejected');
      notifyOrganizer(eventId, events.find(e => e.id === eventId)?.organizerId || '', 'rejected', reason);
    } catch (error) {
      toast.error('Failed to reject event');
    } finally {
      setActionLoading(null);
    }
  };
    
  const handleViewEventDetails = (event: Event) => {
    setModalEvent(event);
    setShowEventModal(true);
  };
  
  useEffect(() => {
    if (showEventModal && modalEvent) {
      const fetchRecommendation = async () => {
        setReviewRecommendation(null); // Clear previous
        const result = await getReviewRecommendation(modalEvent);
        setReviewRecommendation(result);
      };
      fetchRecommendation();
    }
  }, [showEventModal, modalEvent]);
  
  const notifyOrganizer = async (eventId: string, organizerId: string, type: 'approved' | 'rejected', reason?: string) => {
    if (!organizerId) return;
    try {
      await addDoc(collection(db, `users/${organizerId}/notifications`), {
        type: 'event_status',
        message: type === 'approved' 
          ? `Your event has been approved and is now published.`
          : `Your event has been rejected. Reason: ${reason || 'Not provided'}`,
        eventId: eventId,
        read: false,
        createdAt: new Date(),
      });
    } catch (error) {
      console.error('Error sending notification to organizer:', error);
    }
  };

  if (!user || !hasRole('admin')) {
    return <Navigate to="/" />;
  }

  const filteredEvents = events.filter(event => {
    return (
      (!eventSearch || event.title.toLowerCase().includes(eventSearch.toLowerCase()) || (event.organizerName && event.organizerName.toLowerCase().includes(eventSearch.toLowerCase()))) &&
      (!eventStatusFilter || event.status === eventStatusFilter) &&
      (!eventOrganizerFilter || event.organizerName === eventOrganizerFilter || event.organizerId === eventOrganizerFilter)
    );
  });

  const filteredUsers = users.filter(user => {
    return (
      (!userSearch || (user.displayName && user.displayName.toLowerCase().includes(userSearch.toLowerCase())) || (user.email && user.email.toLowerCase().includes(userSearch.toLowerCase()))) &&
      (!userRoleFilter || (['admin', 'organizer', 'user'].includes(userRoleFilter) && user.roles.includes(userRoleFilter as Role)))
    );
  });

  return (
    <div className="w-full max-w-full px-2 sm:px-4 md:px-6 lg:px-8 py-6 sm:py-8 mx-auto">
      <div className="flex flex-col gap-4 sm:gap-8">
        <h1 className="text-3xl font-bold mb-8">Admin Dashboard</h1>
        
        {errorMessage && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            {errorMessage}
            <button 
              onClick={() => fetchData()} 
              className="ml-4 text-red-700 underline hover:text-red-800"
            >
              Retry
            </button>
          </div>
        )}

        <div className="mb-8">
          <TestEventCreation />
        </div>

        <div className="mb-6">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
            <button
              onClick={() => setActiveTab('users')}
              className={`px-4 py-2 rounded ${
                activeTab === 'users'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700'
              }`}
            >
              Users
            </button>
            <button
              onClick={() => setActiveTab('events')}
              className={`px-4 py-2 rounded ${
                activeTab === 'events'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700'
              }`}
            >
              Events
            </button>
            <button
              onClick={() => setActiveTab('pending')}
              className={`px-4 py-2 rounded ${
                activeTab === 'pending'
                  ? 'bg-yellow-600 text-white'
                  : 'bg-gray-200 text-gray-700'
              }`}
            >
              Pending Events
            </button>
            <button
              onClick={() => setActiveTab('ai')}
              className={`px-4 py-2 rounded ${
                activeTab === 'ai'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700'
              }`}
            >
              AI Assistant
            </button>
          </div>
        </div>

        {/* Database Management Panel */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Database Management</h2>
          
          <div className="flex flex-wrap gap-4 mb-4">
            <button
              onClick={handleCheckEvents}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              disabled={isSeeding}
            >
              Check Events
            </button>
            
            <button
              onClick={handleSeedEvents}
              disabled={isSeeding || databaseStatus.hasEvents}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isSeeding ? 'Adding Events...' : 'Seed Sample Events'}
            </button>
            
            <button
              onClick={async () => {
                if (!window.confirm('Are you sure you want to clear all events? This action cannot be undone.')) return;
                
                try {
                  setIsSeeding(true);
                  await clearEvents();
                  setEvents([]);
                  setDatabaseStatus({ checked: true, hasEvents: false });
                  toast.success('All events cleared successfully');
                } catch (error) {
                  console.error('Error clearing events:', error);
                  toast.error('Failed to clear events');
                } finally {
                  setIsSeeding(false);
                }
              }}
              disabled={isSeeding}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              Clear All Events
            </button>
          </div>

          {databaseStatus.checked && (
            <div className={`p-4 rounded ${databaseStatus.hasEvents ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'}`}>
              {databaseStatus.hasEvents 
                ? 'Events exist in the database.' 
                : 'No events found in the database. Click "Seed Sample Events" to add sample data.'}
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Loading data...</span>
          </div>
        ) : activeTab === 'users' ? (
          <div className="overflow-x-auto rounded-lg shadow bg-white dark:bg-gray-900">
            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <input type="text" placeholder="Search users..." value={userSearch} onChange={e => setUserSearch(e.target.value)} className="px-3 py-2 border rounded" />
              <select value={userRoleFilter} onChange={e => setUserRoleFilter(e.target.value)} className="px-3 py-2 border rounded">
                <option value="">All Roles</option>
                <option value="admin">Admin</option>
                <option value="organizer">Organizer</option>
                <option value="user">User</option>
              </select>
            </div>
            <table className="min-w-full bg-white dark:bg-gray-800 rounded-lg overflow-hidden">
              <thead className="bg-gray-100 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Roles
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900 dark:text-white">
                        {user.displayName || 'No Name'}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        ID: {user.id.substring(0, 8)}...
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-wrap gap-1">
                        {user.roles && user.roles.length > 0 ? 
                          user.roles.map(role => (
                            <span 
                              key={role} 
                              className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                            >
                              {role}
                            </span>
                          )) : 
                          <span className="text-sm text-gray-500 dark:text-gray-400">No roles</span>
                        }
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex gap-2">
                        {!user.roles?.includes('admin') && (
                          <button
                            onClick={() => updateUserRole(user.id, 'admin', 'add')}
                            className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded hover:bg-purple-200"
                          >
                            Make Admin
                          </button>
                        )}
                        {!user.roles?.includes('organizer') && (
                          <button
                            onClick={() => updateUserRole(user.id, 'organizer', 'add')}
                            className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded hover:bg-green-200"
                          >
                            Make Organizer
                          </button>
                        )}
                        {user.roles?.includes('organizer') && (
                          <button
                            onClick={() => updateUserRole(user.id, 'organizer', 'remove')}
                            className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded hover:bg-yellow-200"
                          >
                            Remove Organizer
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : activeTab === 'events' ? (
          <div className="overflow-x-auto rounded-lg shadow bg-white dark:bg-gray-900">
            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <input type="text" placeholder="Search events..." value={eventSearch} onChange={e => setEventSearch(e.target.value)} className="px-3 py-2 border rounded" />
              <select value={eventStatusFilter} onChange={e => setEventStatusFilter(e.target.value)} className="px-3 py-2 border rounded">
                <option value="">All Statuses</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="pending_review">Pending Review</option>
                <option value="rejected">Rejected</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <select value={eventOrganizerFilter} onChange={e => setEventOrganizerFilter(e.target.value)} className="px-3 py-2 border rounded">
                <option value="">All Organizers</option>
                {Array.from(new Set(events.map(e => e.organizerName || e.organizerId))).map(org => (
                  <option key={org} value={org}>{org}</option>
                ))}
              </select>
            </div>
            <table className="min-w-full bg-white dark:bg-gray-800 rounded-lg overflow-hidden">
              <thead className="bg-gray-100 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Event
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Organizer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                {filteredEvents.map((event) => (
                  <tr key={event.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900 dark:text-white">
                        {event.title}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        ID: {event.id.substring(0, 8)}...
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {event.organizerName || event.organizerId}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        event.status === 'published' 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                          : event.status === 'draft'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {event.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => deleteEvent(event.id)}
                        className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded hover:bg-red-200"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredEvents.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                      No events found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : activeTab === 'pending' ? (
          <div className="overflow-x-auto rounded-lg shadow bg-white dark:bg-gray-900">
            <table className="min-w-full bg-white dark:bg-gray-800 rounded-lg overflow-hidden">
              <thead className="bg-gray-100 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Event</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Organizer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                {events.filter(e => e.status === 'pending_review').map(event => (
                  <tr key={event.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900 dark:text-white">{event.title}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">ID: {event.id.substring(0, 8)}...</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{event.organizerName || event.organizerId}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">Pending Review</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm flex gap-2">
                      <button onClick={() => handleApproveEvent(event.id)} className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded hover:bg-green-200" disabled={actionLoading === event.id + '-approve'}>{actionLoading === event.id + '-approve' ? 'Approving...' : 'Approve'}</button>
                      <button onClick={() => { setPendingReviewEvent(event); setShowRejectModal(true); }} className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded hover:bg-red-200" disabled={actionLoading === event.id + '-reject'}>{actionLoading === event.id + '-reject' ? 'Rejecting...' : 'Reject'}</button>
                      <button onClick={() => handleViewEventDetails(event)} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded hover:bg-blue-200">View</button>
                    </td>
                  </tr>
                ))}
                {events.filter(e => e.status === 'pending_review').length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-6 py-4 text-center text-gray-500">No pending events</td>
                  </tr>
                )}
              </tbody>
            </table>
            {/* Reject Modal */}
            {showRejectModal && pendingReviewEvent && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
                  <h3 className="text-lg font-semibold mb-4">Reject Event: {pendingReviewEvent.title}</h3>
                  <textarea value={rejectionReason} onChange={e => setRejectionReason(e.target.value)} placeholder="Enter rejection reason (optional)" className="w-full border rounded p-2 mb-4" rows={3} />
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setShowRejectModal(false)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300">Cancel</button>
                    <button onClick={() => handleRejectEvent(pendingReviewEvent.id, rejectionReason)} className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700" disabled={actionLoading === pendingReviewEvent.id + '-reject'}>{actionLoading === pendingReviewEvent.id + '-reject' ? 'Rejecting...' : 'Reject'}</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">AI Assistant Management</h2>
            
            <div className="space-y-6">
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h3 className="text-lg font-medium mb-2">AI Assistant Status</h3>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-sm text-gray-600 dark:text-gray-300">Active and responding to queries</span>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h3 className="text-lg font-medium mb-2">Recent Conversations</h3>
                <div className="space-y-2">
                  <div className="text-sm text-gray-600 dark:text-gray-300">
                    No recent conversations to display
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h3 className="text-lg font-medium mb-2">AI Assistant Settings</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Response Style
                    </label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                      <option value="professional">Professional</option>
                      <option value="friendly">Friendly</option>
                      <option value="concise">Concise</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Response Length
                    </label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                      <option value="short">Short</option>
                      <option value="medium">Medium</option>
                      <option value="detailed">Detailed</option>
                    </select>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="enableEmojis"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="enableEmojis" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                      Enable Emojis in Responses
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="enableTypingIndicator"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      defaultChecked
                    />
                    <label htmlFor="enableTypingIndicator" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                      Show Typing Indicator
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-4">
                <button className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors">
                  Reset to Default
                </button>
                <button className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors">
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}
        {showEventModal && modalEvent && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4 overflow-y-auto">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 w-full max-w-3xl relative">
              <button onClick={() => setShowEventModal(false)} className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-xl">&times;</button>
              
              <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">{modalEvent.title}</h2>
              
              {/* Event Image */}
              {modalEvent.coverImage && (
                <div className="mb-4 rounded-lg overflow-hidden">
                  <img src={modalEvent.coverImage} alt={modalEvent.title || 'Event cover image'} className="w-full h-64 object-cover" />
                </div>
              )}

              {/* Key Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 text-gray-700 dark:text-gray-300">
                <div><strong>Status:</strong> <span className={`px-2 py-1 text-xs rounded-full ${modalEvent.status === 'published' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : modalEvent.status === 'pending_review' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' : modalEvent.status === 'rejected' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'}`}>{modalEvent.status.replace('_', ' ')}</span></div>
                <div><strong>Organizer:</strong> {modalEvent.organizerName || modalEvent.organizerId}</div>
                <div><strong>Category:</strong> {modalEvent.category}</div>
                <div><strong>Created At:</strong> {modalEvent.createdAt?.toDate ? modalEvent.createdAt.toDate().toLocaleString() : modalEvent.createdAt?.toLocaleString()}</div>
                <div><strong>Start:</strong> {modalEvent.startDateTime?.toDate ? modalEvent.startDateTime.toDate().toLocaleString() : modalEvent.startDateTime?.toLocaleString()}</div>
                <div><strong>End:</strong> {modalEvent.endDateTime?.toDate ? modalEvent.endDateTime.toDate().toLocaleString() : modalEvent.endDateTime?.toLocaleString() || 'N/A'}</div>
                <div><strong>Location:</strong> {modalEvent.location?.venue}, {modalEvent.location?.city}</div>
                 {modalEvent.rejectionReason && <div className="col-span-full text-red-600"><strong>Rejection Reason:</strong> {modalEvent.rejectionReason}</div>}
              </div>

              {/* Description */}
              <div className="mb-4 text-gray-700 dark:text-gray-300">
                <h4 className="font-semibold mb-2">Description:</h4>
                <p>{modalEvent.description}</p>
              </div>

              {/* AI Recommendation */}
              {reviewRecommendation && (
                <div className={`mt-4 p-3 rounded ${reviewRecommendation.recommendation === 'approve' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : reviewRecommendation.recommendation === 'reject' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'}`}>
                  <strong className="font-semibold">AI Recommendation:</strong> {reviewRecommendation.recommendation.replace('_', ' ').toUpperCase()}
                  {reviewRecommendation.reason && <span className="ml-2">({reviewRecommendation.reason})</span>}
                </div>
              )}

              {/* Action Buttons */}
              <div className="mt-6 flex justify-end gap-2">
                {modalEvent.status === 'pending_review' && (
                  <>
                    <button onClick={async () => { await handleApproveEvent(modalEvent.id); setShowEventModal(false); await fetchData(); }} className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800 transition-colors">Approve</button>
                    <button onClick={() => { setPendingReviewEvent(modalEvent); setShowRejectModal(true); setShowEventModal(false); }} className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800 transition-colors">Reject</button>
                  </>
                )}
                 <button onClick={() => setShowEventModal(false)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 transition-colors">Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard; 